import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Spinner } from 'react-bootstrap';
import { MdClose, MdOutlineAccessTime } from 'react-icons/md';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import Select, { styles, DropdownIndicator } from 'components/inputs/Select';
import { CREATE_STATUS_CODE } from 'components/common/constant';
import { API } from 'api';
import { CHECK_FLEET_RESERVATION_AVAILABILITY, CREATE_FLEET_RESERVATION } from 'actions/fleetReservation';
import {
  BUFFER_MINUTES,
  TARGET_TYPES,
  REPEAT_TYPES,
  DAY_CHIPS,
  isRecurring,
  scheduleErrors,
  scheduleSummary,
  formatDate,
  slotText,
  emptySelection,
  hasSelection,
  describeResources,
  pruneUnavailable,
  buildFleetOptions,
  normalizeAvailability,
  normalizeChargerList,
  asArray,
  unwrap,
  loadStations,
  toStationOptions,
  buildAvailabilityPayload,
  buildCreatePayload,
  DateField,
  TimeField,
} from './fleetReservationUtils';

const initialSchedule = { repeatType: 'ONCE', startDate: '', endDate: '', daysOfWeek: [], timeFrom: '', timeTo: '' };

const ReviewItem = ({ label, children, className = '' }) => (
  <div className="fr-review__item">
    <div className="fr-label">{label}</div>
    <div className={className}>{children || '—'}</div>
  </div>
);

ReviewItem.propTypes = { label: PropTypes.string, children: PropTypes.node, className: PropTypes.string };

const FleetReservationCreate = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isAvailabilityLoading, isSaving } = useSelector((state) => state.fleetReservation);

  const [stations, setStations] = useState([]);
  const [isStationsLoading, setIsStationsLoading] = useState(true);
  const [stationId, setStationId] = useState('');
  const [fleet, setFleet] = useState(null);
  const [schedule, setSchedule] = useState(initialSchedule);
  const [tree, setTree] = useState([]);
  const [baseTree, setBaseTree] = useState([]);
  const [isChargersLoading, setIsChargersLoading] = useState(false);
  const [selection, setSelection] = useState(emptySelection());
  const [reason, setReason] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [slotDates, setSlotDates] = useState(null);

  const stationOptions = useMemo(() => toStationOptions(stations), [stations]);
  const stationName = _.get(_.find(stationOptions, { value: stationId }), 'label', '');
  const errors = scheduleErrors(schedule);
  const visibleErrors = showErrors ? errors : {};
  const scheduleReady = _.isEmpty(errors);
  const summary = useMemo(() => scheduleSummary(schedule), [schedule]);
  const dateCount = slotDates ? slotDates.length : summary.occurrences;
  const isWeekly = schedule.repeatType === 'WEEKLY';
  const isCheckingAvailability = !!stationId && scheduleReady && (!availabilityChecked || isAvailabilityLoading);
  const canSubmit = !!fleet && !!stationId && scheduleReady && availabilityChecked && !isAvailabilityLoading && hasSelection(selection, tree);

  useEffect(() => {
    loadStations()
      .then((list) => setStations(list.filter((item) => item.access_type === 'Public' && !item.is_external_station)))
      .catch(() => setStations([]))
      .finally(() => setIsStationsLoading(false));
  }, []);

  useEffect(() => {
    setTree([]);
    setBaseTree([]);
    setSelection((prev) => emptySelection(prev.mode));
    if (!stationId) return undefined;
    let active = true;
    setIsChargersLoading(true);
    API.getChargersList({ id: stationId, request_all: true })
      .then((res) => active && setBaseTree(normalizeChargerList(asArray(unwrap(_.get(res, 'data'))))))
      .catch(() => active && setBaseTree([]))
      .finally(() => active && setIsChargersLoading(false));
    return () => {
      active = false;
    };
  }, [stationId]);

  const loadFleetOptions = useMemo(
    () =>
      _.debounce((search, callback) => {
        API.getFleetReservationFleetOptions({ search, limit: 20 })
          .then((res) => callback(buildFleetOptions(asArray(unwrap(_.get(res, 'data'))))))
          .catch(() => callback([]));
      }, 300),
    []
  );

  const checkAvailability = useMemo(
    () =>
      _.debounce((id, sched) => {
        dispatch({
          type: CHECK_FLEET_RESERVATION_AVAILABILITY.REQUEST,
          payload: buildAvailabilityPayload(id, sched),
          cb: (res) => {
            setAvailabilityChecked(true);
            if (_.get(res, 'status') !== 200) {
              setTree([]);
              setAvailabilityError(_.get(res, 'data.message') || 'Could not check availability. Adjust the schedule and try again.');
              return;
            }
            const data = unwrap(_.get(res, 'data'));
            const nextTree = normalizeAvailability(data);
            setAvailabilityError('');
            setSlotDates(_.get(data, 'dates', null));
            setTree(nextTree);
            setSelection((prev) => pruneUnavailable(prev, nextTree));
          },
        });
      }, 250),
    []
  );

  useEffect(
    () => () => {
      checkAvailability.cancel();
      loadFleetOptions.cancel();
    },
    []
  );

  useEffect(() => {
    setConflicts([]);
    setAvailabilityChecked(false);
    setAvailabilityError('');
    setSlotDates(null);
    if (stationId && scheduleReady) checkAvailability(stationId, schedule);
  }, [stationId, schedule]);

  const setScheduleField = (key, value) => setSchedule((prev) => ({ ...prev, [key]: value }));
  const setRepeatType = (repeatType) =>
    setSchedule((prev) => ({
      ...prev,
      repeatType,
      endDate: repeatType === 'ONCE' ? '' : prev.endDate,
      daysOfWeek: repeatType === 'WEEKLY' ? prev.daysOfWeek : [],
    }));
  const toggleDay = (day) =>
    setScheduleField('daysOfWeek', schedule.daysOfWeek.includes(day) ? schedule.daysOfWeek.filter((d) => d !== day) : [...schedule.daysOfWeek, day]);

  const submit = (addAnother) => {
    setShowErrors(true);
    if (!canSubmit) return;
    dispatch({
      type: CREATE_FLEET_RESERVATION.REQUEST,
      payload: buildCreatePayload({ stationId, schedule, fleet, selection, reason }),
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE) {
          const code = _.get(unwrap(_.get(res, 'data')), 'reservationCode', '');
          toast.success(_.get(res, 'data.message') || `Reservation ${code} created`);
          if (addAnother) {
            setStationId('');
            setReason('');
            setShowErrors(false);
          } else {
            onCreated(code);
          }
          return;
        }
        setConflicts(_.get(res, 'data.conflicts', []));
      },
    });
  };

  const submitHint = () => {
    if (!stationId) return 'Select a station';
    if (!fleet) return 'Select a fleet';
    if (!scheduleReady) return 'Complete the schedule';
    if (isCheckingAvailability) return 'Checking availability…';
    if (availabilityError) return 'Availability check failed';
    if (!hasSelection(selection, tree)) return selection.mode === 'STATION' ? 'No charger is free for this schedule' : 'Select an available resource';
    return '';
  };

  const renderAvailability = () => {
    if (!stationId) return <div className="fr-empty-note">Select a station above to see its chargers.</div>;
    const isLive = availabilityChecked && !availabilityError && !isAvailabilityLoading;
    const rows = isLive ? tree : baseTree;
    let note = null;
    if (!scheduleReady) note = <div className="fr-muted small mb-2">Complete the schedule above to see which chargers are free.</div>;
    else if (isCheckingAvailability) {
      note = (
        <div className="fr-muted small mb-2">
          <Spinner as="span" animation="border" size="sm" className="me-2" />
          Checking availability…
        </div>
      );
    } else if (availabilityError) note = <div className="fr-empty-note fr-empty-note--error mb-2">{availabilityError}</div>;

    if (rows.length === 0) {
      if (isChargersLoading) {
        return (
          <div className="fr-empty-note">
            <Spinner as="span" animation="border" size="sm" className="me-2" />
            Loading chargers…
          </div>
        );
      }
      return note || <div className="fr-empty-note">No chargers found at this station.</div>;
    }

    const isEntire = selection.mode === 'STATION';
    const status = (item) =>
      item.isAvailable ? (
        <span className="fr-ok">✓ Available · {slotText(slotDates, summary.blocked)}</span>
      ) : (
        <span className="fr-blocked">⚠ {item.conflictLabel || 'Reserved'}</span>
      );

    const selectionHint = () => {
      const free = rows.filter((charger) => charger.isAvailable);
      const anyConnectorFree = rows.some((charger) => charger.connectors.some((connector) => connector.isAvailable));
      if (!isLive) {
        return isEntire
          ? 'All chargers at this station will be reserved. Set the date and time above to check availability.'
          : 'Select a charger or a single connector below. Set the date and time above to check availability.';
      }
      if (isEntire) {
        if (!free.length) return 'No charger is available at this station for the selected time. Please change the date or time.';
        if (free.length < rows.length)
          return 'Some chargers are already reserved for the selected time. Only the available chargers will be reserved.';
        return 'All chargers at this station are available and will be reserved for the selected time.';
      }
      if (!anyConnectorFree) return 'No charger or connector is available for the selected time. Please change the date or time.';
      const picked = _.find(rows, { chargerId: selection.chargerId });
      if (picked && selection.mode === 'CHARGER') return `Charger ${picked.ocppId} will be fully reserved for the selected time.`;
      if (picked)
        return `Only connector ${_.get(_.find(picked.connectors, { connectorId: selection.connectorId }), 'name') || selection.connectorId} of charger ${picked.ocppId} will be reserved for the selected time.`;
      const partial = rows.find((charger) => !charger.isAvailable && charger.connectors.some((connector) => connector.isAvailable));
      if (partial) {
        return `Charger ${partial.ocppId} cannot be fully reserved for the selected time. Please select an available connector below to continue.`;
      }
      return 'Select a charger to reserve all its connectors, or select a single connector.';
    };

    return (
      <>
        {note}
        <div className="fr-select-hint">{selectionHint()}</div>
        <div className="fr-box p-0">
          {rows.map((charger) => {
            const chargerPicked = selection.mode === 'CHARGER' && selection.chargerId === charger.chargerId;
            const chargerHeld = isEntire && (!isLive || charger.isAvailable);
            return (
              <React.Fragment key={charger.chargerId}>
                <label className="fr-tree-row">
                  <input
                    type="checkbox"
                    checked={chargerHeld || chargerPicked}
                    disabled={isEntire || !charger.isAvailable}
                    onChange={() =>
                      setSelection(chargerPicked ? emptySelection('CHARGER') : { mode: 'CHARGER', chargerId: charger.chargerId, connectorId: null })
                    }
                  />
                  <b>{charger.ocppId}</b>
                  {(chargerHeld || chargerPicked) && <span className="fr-muted small">whole charger — all connectors held</span>}
                  {isLive && (
                    <span className="ms-auto small">
                      {chargerHeld ? (
                        <span className="fr-ok">✓ Will be reserved · {slotText(slotDates, summary.blocked)}</span>
                      ) : (
                        !charger.isAvailable && status(charger)
                      )}
                    </span>
                  )}
                </label>
                {charger.connectors.map((connector) => {
                  const picked =
                    selection.mode === 'CONNECTOR' && selection.chargerId === charger.chargerId && selection.connectorId === connector.connectorId;
                  const held = chargerHeld || chargerPicked;
                  return (
                    <label key={connector.connectorId} className={`fr-tree-row fr-tree-row--child ${held ? 'opacity-50' : ''}`}>
                      <input
                        type="checkbox"
                        checked={held || picked}
                        disabled={isEntire || held || !connector.isAvailable}
                        onChange={() =>
                          setSelection(
                            picked
                              ? emptySelection('CHARGER')
                              : { mode: 'CONNECTOR', chargerId: charger.chargerId, connectorId: connector.connectorId }
                          )
                        }
                      />
                      <span>{connector.name || connector.connectorId}</span>
                      <span className="fr-muted small">{[connector.type, connector.power].filter(Boolean).join(' · ')}</span>
                      {isLive && <span className="ms-auto small">{status(connector)}</span>}
                    </label>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </>
    );
  };

  const hint = submitHint();

  return (
    <Modal show centered size="xl" scrollable onHide={onClose} dialogClassName="fr-create-modal">
      <div className="fr-page fr-create-modal__body">
        <div className="fr-modal-header">
          <h5 className="mb-0">{t('header.createFleetReservation', 'Create Reservation')}</h5>
          <MdClose size={28} color="#BE210B" className="fr-modal-header__close" onClick={onClose} />
        </div>
        <div className="row g-3">
          <div className="col-12 col-lg-8">
            <Card>
              <div className="fr-section-title">
                <span className="fr-step">1</span> Reservation for
              </div>
              <div className="fr-field">
                <div className="fr-label">Fleet</div>
                <AsyncSelect
                  className="form-field react-select"
                  classNamePrefix="react-select"
                  styles={styles()}
                  components={{ DropdownIndicator }}
                  cacheOptions
                  defaultOptions
                  loadOptions={loadFleetOptions}
                  value={fleet}
                  onChange={setFleet}
                  placeholder="Search by name or phone"
                  noOptionsMessage={() => 'No fleet found'}
                />
                {showErrors && !fleet && <div className="text-danger small mt-1">Select a fleet</div>}
              </div>
            </Card>

            <Card>
              <div className="fr-section-title">
                <span className="fr-step">2</span> Schedule
                <span className="fr-section-hint">Set the time first, so availability shows on each charger below.</span>
              </div>

              <div className="fr-segment mb-3">
                {REPEAT_TYPES.map((type) => (
                  <button
                    type="button"
                    key={type.value}
                    className={schedule.repeatType === type.value ? 'active' : ''}
                    onClick={() => setRepeatType(type.value)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              <div className="fr-schedule-grid">
                <div>
                  <div className="fr-label">Start date</div>
                  <DateField
                    value={schedule.startDate}
                    min={moment().format('YYYY-MM-DD')}
                    onChange={(value) => setScheduleField('startDate', value)}
                  />
                  {visibleErrors.startDate && <div className="text-danger small">{visibleErrors.startDate}</div>}
                </div>
                {isRecurring(schedule.repeatType) && (
                  <div>
                    <div className="fr-label">End date</div>
                    <DateField
                      value={schedule.endDate}
                      min={schedule.startDate || moment().format('YYYY-MM-DD')}
                      onChange={(value) => setScheduleField('endDate', value)}
                    />
                    {visibleErrors.endDate && <div className="text-danger small">{visibleErrors.endDate}</div>}
                  </div>
                )}
                <div>
                  <div className="fr-label">Time from</div>
                  <TimeField value={schedule.timeFrom} onChange={(value) => setScheduleField('timeFrom', value)} />
                  {visibleErrors.timeFrom && <div className="text-danger small">{visibleErrors.timeFrom}</div>}
                </div>
                <div>
                  <div className="fr-label">Time to</div>
                  <TimeField value={schedule.timeTo} onChange={(value) => setScheduleField('timeTo', value)} />
                  {visibleErrors.timeTo && <div className="text-danger small">{visibleErrors.timeTo}</div>}
                </div>
                <div>
                  <div className="fr-label">Pre-arrival buffer</div>
                  <input className="form-control fr-input" value={`${BUFFER_MINUTES} minutes`} disabled />
                </div>
              </div>

              <div className={`mt-3 ${isWeekly ? '' : 'opacity-50'}`}>
                <div className="fr-label">Repeat on</div>
                <div className="fr-day-chips">
                  {DAY_CHIPS.map((chip) => (
                    <button
                      type="button"
                      key={chip.value}
                      title={chip.name}
                      disabled={!isWeekly}
                      className={isWeekly && schedule.daysOfWeek.includes(chip.value) ? 'active' : ''}
                      onClick={() => toggleDay(chip.value)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
                {visibleErrors.daysOfWeek && <div className="text-danger small">{visibleErrors.daysOfWeek}</div>}
              </div>

              {summary.booked && (
                <div className="fr-summary-bar">
                  <MdOutlineAccessTime className="fr-blocked me-2" size={16} />
                  <span>
                    Booked <b>{summary.booked}</b>
                  </span>
                  <span>
                    Public blocked from <b className="fr-blocked">{summary.blockedFrom}</b>
                  </span>
                  {dateCount > 0 && <span>{summary.repeat}</span>}
                  {dateCount > 0 && (
                    <span>
                      <b>{dateCount}</b> occurrence{dateCount === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              )}
            </Card>

            <Card>
              <div className="fr-section-title">
                <span className="fr-step">3</span> Station and resources
              </div>
              <div className="fr-field">
                <Select
                  label="Charging station — one per reservation"
                  placeholder={isStationsLoading ? 'Loading stations…' : 'Select station'}
                  isLoading={isStationsLoading}
                  options={stationOptions}
                  disableSort
                  value={stationId}
                  onChange={(value) => setStationId(value || '')}
                  error={showErrors && !stationId ? 'Select a station' : ''}
                />
              </div>
              <div className="fr-segment fr-segment--radio mb-3">
                {TARGET_TYPES.map((type) => {
                  const active = type.value === 'STATION' ? selection.mode === 'STATION' : selection.mode !== 'STATION';
                  return (
                    <button
                      type="button"
                      key={type.value}
                      className={active ? 'active' : ''}
                      onClick={() => !active && setSelection(emptySelection(type.value))}
                    >
                      {type.label}
                    </button>
                  );
                })}
              </div>

              {renderAvailability()}

              <div className="mt-3">
                <div className="fr-label">Reason / remarks (optional)</div>
                <input className="form-control fr-input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            </Card>
          </div>

          <div className="col-12 col-lg-4">
            <div className="fr-review-col">
              <div className="fr-review">
                <h5>Review</h5>
                <ReviewItem label="Station">{stationName}</ReviewItem>
                <ReviewItem label="Fleet">{_.get(fleet, 'label', '')}</ReviewItem>
                <ReviewItem label="Resource">
                  {stationId && availabilityChecked && describeResources(selection, tree).map((item) => <div key={item}>{item}</div>)}
                </ReviewItem>
                <ReviewItem label="Booked window">{summary.booked}</ReviewItem>
                <ReviewItem label="Public blocked from" className="fr-blocked">
                  {summary.blockedFrom}
                </ReviewItem>
                <ReviewItem label="Repeat">{dateCount > 0 && summary.repeat}</ReviewItem>
                <ReviewItem label="Occurrences">{dateCount > 0 && `${dateCount} date${dateCount === 1 ? '' : 's'}`}</ReviewItem>
                {conflicts.length > 0 ? (
                  <div className="fr-review__bad">
                    {conflicts.map((conflict) => (
                      <div key={`${conflict.reservationCode}-${conflict.reservationDate}`}>
                        {formatDate(conflict.reservationDate)} conflicts with {conflict.reservationCode}
                      </div>
                    ))}
                  </div>
                ) : (
                  canSubmit && <div className="fr-review__ok">✓ Available for the selected schedule</div>
                )}
              </div>
              <div className="fr-review__actions">
                {hint && <div className="fr-muted small text-center">{hint}</div>}
                <Button disabled={!canSubmit || isSaving} onClick={() => submit(false)}>
                  {(isCheckingAvailability || isSaving) && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                  Create Reservation
                </Button>
                <button type="button" className="btn fr-btn-danger" disabled={!canSubmit || isSaving} onClick={() => submit(true)}>
                  Save and Add Another
                </button>
                <button type="button" className="btn fr-btn-outline" onClick={onClose}>
                  Cancel
                </button>
              </div>
              <div className="fr-review__note">
                A reservation covers one station. To book another station, use <b>Save and Add Another</b> — fleet and schedule are kept.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

FleetReservationCreate.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default FleetReservationCreate;
